import type { ReactElement } from 'react'

/**
 * A small, self-contained icon set.
 *
 * Deliberately not emoji: emoji render differently on every platform, cannot be
 * recoloured, and do not scale with the type system. These are stroked paths on
 * one 24x24 grid with a single 1.75 stroke width and round caps, so they stay
 * visually consistent with each other and inherit colour from their parent.
 */
const PATHS: Record<string, ReactElement> = {
  coin: (
    <>
      <ellipse cx="12" cy="7.5" rx="7.5" ry="3.5" />
      <path d="M4.5 7.5v9c0 1.93 3.36 3.5 7.5 3.5s7.5-1.57 7.5-3.5v-9" />
      <path d="M4.5 12c0 1.93 3.36 3.5 7.5 3.5s7.5-1.57 7.5-3.5" />
    </>
  ),
  users: (
    <>
      <path d="M15.5 20v-1.6a3.6 3.6 0 0 0-3.6-3.6H6.6A3.6 3.6 0 0 0 3 18.4V20" />
      <circle cx="9.25" cy="7.5" r="3.5" />
      <path d="M21 20v-1.6a3.6 3.6 0 0 0-2.7-3.48" />
      <path d="M15.75 4.13a3.5 3.5 0 0 1 0 6.74" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.25V12l3 1.75" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
      <path d="M7 5.5H4.5v1a3.5 3.5 0 0 0 3 3.46" />
      <path d="M17 5.5h2.5v1a3.5 3.5 0 0 1-3 3.46" />
      <path d="M9.5 20h5M12 14v6" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.75" fill="currentColor" />
    </>
  ),
  bolt: <path d="M13.5 3 5.5 13.5h5L10 21l8.5-10.5h-5.2z" />,
  arrowRight: (
    <>
      <path d="M4.5 12h15" />
      <path d="m13.5 6 6 6-6 6" />
    </>
  ),
  arrowLeft: (
    <>
      <path d="M19.5 12h-15" />
      <path d="m10.5 6-6 6 6 6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="m4.5 12.5 5 5 10-11" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M15 6.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.5" />
    </>
  ),
  logout: (
    <>
      <path d="M15 4.5h2.5A2.5 2.5 0 0 1 20 7v10a2.5 2.5 0 0 1-2.5 2.5H15" />
      <path d="M10 16.5 14.5 12 10 7.5" />
      <path d="M14.5 12H4" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m3.5 7 7.36 5.15a2 2 0 0 0 2.28 0L20.5 7" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10" width="15" height="10" rx="2.5" />
      <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M9.9 5.7A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3 3.85" />
      <path d="M6.3 7.6A16.9 16.9 0 0 0 2.5 12S6 18.5 12 18.5a9.3 9.3 0 0 0 3.9-.84" />
      <path d="M10 10a2.8 2.8 0 0 0 4 4" />
      <path d="m4 4 16 16" />
    </>
  ),
  crown: <path d="M4 17.5 5 7l4.5 3.5L12 5l2.5 5.5L19 7l1 10.5z" />,
  wifiOff: (
    <>
      <path d="M12 18.5h.01" />
      <path d="M8.6 15.1a4.8 4.8 0 0 1 5.6-.8" />
      <path d="M5.2 11.6a9.6 9.6 0 0 1 4-2.3" />
      <path d="M18.8 11.6a9.6 9.6 0 0 0-3-1.98" />
      <path d="m3 3 18 18" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M19.8 4.5v4.2h-4.2" />
    </>
  ),
}

export type IconName = keyof typeof PATHS

export function Icon({
  name,
  size = 20,
  className,
  label,
}: {
  name: IconName
  size?: number
  className?: string
  label?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  )
}

/** Indeterminate progress. Respects reduced motion via the global CSS override. */
export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      className={className}
      role="status"
      aria-label="Loading"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2.5" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        style={{ transformOrigin: '12px 12px', animation: 'clx-spin 720ms linear infinite' }}
      />
      <style>{`@keyframes clx-spin { to { transform: rotate(360deg) } }`}</style>
    </svg>
  )
}
